import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Quote, ArrowRight, BookOpen, Calendar, Users, TrendingUp,
  Play, Pause, Volume2, Maximize, Clock, ListVideo,
} from 'lucide-react';
import {
  Eyebrow, RaceBib, Tom, Stat, EvidenceCard, MarginNote, MarkerHighlight,
} from '@/components/foundry';

interface Chapter {
  time: string;
  label: string;
}

interface VideoModule {
  /** Two-digit episode number, used as the player watermark + nav numbering. */
  num: string;
  title: string;
  duration: string;
  /** Whose voice is in the video — drives the speaker label below the player. */
  speaker: 'marc' | 'aimee' | 'tom' | 'marc-aimee';
  hook: string;
  chapters: Chapter[];
}

interface Section {
  id: string;
  eyebrow: string;
  title: string;
  video: VideoModule;
}

const SECTIONS: Section[] = [
  {
    id: 'context',
    eyebrow: 'WHO + WHAT',
    title: 'Context: Project Junior Pro Team',
    video: {
      num: '01',
      title: 'Meet Marc + Aimee — the brand before the rebuild',
      duration: '4:12',
      speaker: 'marc-aimee',
      hook: 'Two coaches, a £190k kids-football brand, a Skool community, and a sales call as the default conversion. The shape of the problem before AI changed it.',
      chapters: [
        { time: '0:00', label: 'Who we are + who we coach' },
        { time: '1:18', label: 'The funnel we inherited' },
        { time: '2:45', label: 'Why we knew something had to change' },
      ],
    },
  },
  {
    id: 'old-stack',
    eyebrow: 'BEFORE',
    title: 'The pre-AI stack (and why it leaked)',
    video: {
      num: '02',
      title: 'Walking through the broken funnel — 3 leak points',
      duration: '6:34',
      speaker: 'marc',
      hook: 'A live walk-through of the old PJPT funnel: the static landing page, the calendly link, the 47-lesson course library, and the parents\' Skool group. Where each one leaked.',
      chapters: [
        { time: '0:00', label: 'The acquisition leak — proof never arrived' },
        { time: '2:08', label: 'The activation leak — 47 lessons, no path' },
        { time: '4:12', label: 'The retention leak — answers, no routing' },
      ],
    },
  },
  {
    id: 'redesign',
    eyebrow: 'WHAT WE BUILT',
    title: 'The AI-native redesign',
    video: {
      num: '03',
      title: 'Inside the rebuild — Acquisition + Coaching OS together',
      duration: '11:08',
      speaker: 'marc-aimee',
      hook: 'The two-product pattern from the Academy, applied to a kids-football brand. What we shipped, what we left alone, and what the bridge page actually said to parents.',
      chapters: [
        { time: '0:00', label: 'What the Game Plan locked down first' },
        { time: '2:24', label: 'Kickstart funnel for parents' },
        { time: '5:01', label: 'The bridge page — the personalised reply' },
        { time: '7:48', label: 'Coaching OS — the first 30-day path' },
        { time: '9:33', label: 'How the AI co-coach prepares Marc' },
      ],
    },
  },
  {
    id: 'results',
    eyebrow: 'PROOF',
    title: 'Results · 90 days post-rebuild',
    video: {
      num: '04',
      title: '90-day verdict — the numbers + what surprised us',
      duration: '5:47',
      speaker: 'aimee',
      hook: 'Sales calls to zero, enrollments at 2.4×, member completion at 78%. The unexpected one: revenue per coaching hour up 2.1× because Marc shifted from screening to depth.',
      chapters: [
        { time: '0:00', label: 'Sales calls: 18/mo → 0' },
        { time: '1:42', label: 'Enrollment lift on the same ad spend' },
        { time: '3:15', label: 'Member 30-day completion — the chart' },
        { time: '4:30', label: 'The depth-not-time shift (the unexpected one)' },
      ],
    },
  },
  {
    id: 'replicable',
    eyebrow: 'NEXT',
    title: 'What\'s replicable for your business',
    video: {
      num: '05',
      title: 'Tom\'s takeaway — the portable architecture',
      duration: '3:55',
      speaker: 'tom',
      hook: 'The mechanics aren\'t portable — you\'re not coaching kids\' football. The architecture is. One acquisition entry point, one first experience, one weekly cadence.',
      chapters: [
        { time: '0:00', label: 'Three slots, your business, the most-leverage rebuild' },
        { time: '1:20', label: 'Why the Game Plan comes first' },
        { time: '2:45', label: 'How to spot your equivalent of the bridge page' },
      ],
    },
  },
];

const SPEAKER_LABEL: Record<VideoModule['speaker'], string> = {
  marc: 'Marc · co-founder, PJPT',
  aimee: 'Aimee · co-founder, PJPT',
  'marc-aimee': 'Marc + Aimee · co-founders, PJPT',
  tom: 'Tom · AI Brand Lab',
};

/**
 * CaseStudy — long-form PJPT proof spine. The single most-named case
 * inside Academy + Lab + workspace: the canonical example of what an
 * AI-native coaching business actually looks like once it ships.
 *
 * Layout: TOC sidebar (sticky) + reading column. Single hero pull-quote.
 * Embedded stats use the foundry Stat primitive. Sections call back to
 * the visual spine motifs (Glass Box, Signal Radar, GPS) by reference,
 * not by re-rendering them — this is reading, not interactive.
 */
export default function CaseStudy() {
  return (
    <div className="mx-auto max-w-[1280px] px-8 py-10">
      {/* Page head */}
      <header className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
        <div>
          <Eyebrow className="mb-2">CASE STUDY · PROJECT JUNIOR PRO TEAM (PJPT) · COHORT 03</Eyebrow>
          <h1 className="font-geist text-[36px] font-medium leading-[1.05] tracking-[-0.025em] [text-wrap:balance]">
            How a 2-coach kids-football brand rebuilt to{' '}
            <MarkerHighlight tone="race" duration={900} delay={300}>
              <span>no-sales-call enrollment</span>
            </MarkerHighlight>
            {' '}in 90 days.
          </h1>
          <p className="mt-3 max-w-[640px] text-[15px] leading-[1.55] text-fd-text-muted">
            What changed when PJPT redesigned acquisition + delivery around AI. The exact
            stack, the parts that broke, the gates that worked, and what's portable for
            your business.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <RaceBib n="" label="90-day verdict" tone="race" size="md" shadow="sm" />
          <span className="inline-flex items-center gap-1.5 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
            <ListVideo className="h-3.5 w-3.5" />
            {SECTIONS.length} video modules · ~{Math.round(SECTIONS.reduce((s, sec) => s + parseDuration(sec.video.duration), 0) / 60)} min total
          </span>
        </div>
      </header>

      {/* Hero stat band */}
      <section className="mb-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Sales calls / month" value="0" delta="Was: 18 calls / mo" deltaTone="ok" />
        <Stat label="Enrollment lift" value="2.4×" delta="vs. cohort 02" deltaTone="ok" />
        <Stat label="Days to first AI experience live" value="11" delta="Workshop 4 verdict: ship" deltaTone="ok" />
      </section>

      {/* Body grid: TOC + reading */}
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[200px_1fr]">
        {/* Sticky TOC — doubles as a video index */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Eyebrow className="mb-3 flex items-center gap-1.5">
            <ListVideo className="h-3.5 w-3.5" />
            EPISODES
          </Eyebrow>
          <nav className="grid gap-1.5">
            {SECTIONS.map((s, i) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="group flex items-baseline gap-2.5 rounded-[6px] px-2.5 py-1.5 transition-colors hover:bg-fd-surface-1"
              >
                <span className="w-5 shrink-0 font-geist-mono text-[11px] font-semibold tracking-tight text-fd-text-subtle">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium leading-[1.3] text-fd-text-muted group-hover:text-fd-text">
                    {s.title.replace(/^[A-Z]+: /, '')}
                  </span>
                  <span className="mt-0.5 block font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
                    {s.video.duration}
                  </span>
                </div>
              </a>
            ))}
          </nav>
          <div className="mt-6 rounded-[10px] border border-dashed border-fd-border-strong/40 bg-fd-paper p-3">
            <Tom variant="walking-on" className="mx-auto h-20 w-auto" />
            <p className="mt-2 text-center font-geist-mono text-[10px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
              Tom recorded · 2026-04-14
            </p>
          </div>
        </aside>

        {/* Reading column */}
        <article className="prose-fd max-w-[680px]">
          <Section section={SECTIONS[0]}>
            <p>
              Project Junior Pro Team (PJPT) is a kids-football coaching brand. Two coaches —
              Marc and Aimee — running a 12-week development programme for junior players, plus a
              parents' community for stickiness. Annual revenue around <Inline>£190k</Inline> when
              we started. They had a Skool community, an old course site, three different lead
              forms, and a sales call as the default conversion mechanism.
            </p>
            <PullQuote attribution="Marc — co-founder, PJPT">
              We were spending more time on calls than coaching. Every parent wanted reassurance
              we were "the right one" for their kid. We didn't have a way to give them that
              before the call.
            </PullQuote>
          </Section>

          <Section section={SECTIONS[1]}>
            <p>
              The leaks were predictable. Cold paid traffic landed on a static page. Interested
              parents booked a call. Marc spent 30 minutes pre-qualifying, 30 minutes selling,
              30 minutes onboarding — for one enrollment. The community was lively but generic;
              the course library had 47 lessons and most parents stopped at lesson two.
            </p>
            <p>
              Three things going wrong (the same three the Game Plan looks for in your business):
            </p>
            <ul>
              <li>
                <b>Acquisition leak:</b> the static page didn't carry their proof — Marc's
                belief in the method only landed in conversation.
              </li>
              <li>
                <b>Activation leak:</b> the onboarding sequence assumed parents knew which lesson
                to start from. Most didn't.
              </li>
              <li>
                <b>Retention leak:</b> the community gave answers, but didn't<em> route </em>
                parents to the next move for their specific kid.
              </li>
            </ul>
          </Section>

          <Section section={SECTIONS[2]}>
            <p>
              We rebuilt around the two-product pattern the Academy teaches:
            </p>
            <p>
              <b>AI Acquisition OS:</b> a Kickstart funnel for parents (5 short videos + a
              progress unlock at the end), followed by an AI-personalised "warm bridge" that
              read the Kickstart submissions and proposed the right path forward — no sales
              call required for parents who self-qualified.
            </p>
            <p>
              <b>AI Coaching OS:</b> the first 30-day path for new members became a
              progressive AI experience. The kid's age + position + current ability fed an AI
              co-coach that surfaced the right two lessons each week and prepped Marc for the
              live group call.
            </p>
            <PullQuote attribution="Aimee — co-founder, PJPT">
              The thing nobody tells you about going AI-native is how much work the diagnostic is.
              We spent two weeks on the Game Plan. By the end we knew exactly what we were rebuilding
              and what we were leaving alone.
            </PullQuote>
          </Section>

          <Section section={SECTIONS[3]}>
            <p>
              90 days post-launch:
            </p>
            <ul>
              <li>Sales calls dropped from <Inline>18/mo</Inline> to <Inline>0</Inline>.</li>
              <li>Enrollments lifted to <Inline>2.4×</Inline> the prior cohort, on the same ad
                spend.</li>
              <li>Member 30-day completion rate for the first AI experience: <Inline>78%</Inline>{' '}
                vs. <Inline>22%</Inline> for the old course path.</li>
              <li>Marc's coaching hours shifted from one-to-one screening to small-group depth
                work; revenue per hour went up <Inline>2.1×</Inline>.</li>
            </ul>
            <EvidenceCard kind="verdict" eyebrow="TOM'S 90-DAY READ">
              PJPT shows what happens when AI sits inside the funnel and the experience —
              not on top of it as a chatbot — coaches stop competing on time and start
              competing on judgment. Marc's depth is now the product.
            </EvidenceCard>
          </Section>

          <Section section={SECTIONS[4]}>
            <p>
              Most of PJPT's pattern is portable. The exact mechanics aren't —
              you're not coaching kids' football. But the architecture is:
            </p>
            <ul>
              <li>
                <b>One acquisition entry point</b> that does the qualifying + warming work
                AI is good at, before any human conversation.
              </li>
              <li>
                <b>One first experience</b> that you hand-pick, and the AI
                personalises per member.
              </li>
              <li>
                <b>One weekly cadence</b> where you review signal and route the
                next week — not 12 dashboards.
              </li>
            </ul>
            <p>
              That's the Game Plan's job: tell you which of those three slots, for{' '}
              <em>your</em> business, is the most-leverage rebuild. Then your AI Co-Founder runs the
              AI Acquisition OS skills to build it.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="?surface=os-v2"
                className="inline-flex h-11 items-center gap-2 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent px-5 font-geist-mono text-[13px] font-bold uppercase tracking-[0.04em] text-fd-text transition-all hover:-translate-x-px hover:-translate-y-px"
                style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
              >
                Open my Game Plan <ArrowRight className="h-4 w-4" />
              </a>
              <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
                · or read the next case study (coming Q2)
              </span>
            </div>
          </Section>

          <MarginNote tilt={-3} tone="teal" size="sm" className="mt-8 inline-block">
            this is the case tom comes back to most.
          </MarginNote>
        </article>
      </div>
    </div>
  );
}

// ── Section wrapper ─────────────────────────────────────────────────────

function Section({ section, children }: { section: Section; children: React.ReactNode }) {
  return (
    <motion.section
      id={section.id}
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-15%' }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="mb-14 scroll-mt-24"
    >
      <Eyebrow className="mb-2 block">{section.eyebrow}</Eyebrow>
      <h2 className="mb-5 font-geist text-[24px] font-medium leading-[1.15] tracking-[-0.02em] text-fd-text">
        {section.title}
      </h2>

      {/* Video module — every section opens with the video, body text reads
          underneath as the written companion. */}
      <CaseStudyVideoModule video={section.video} />

      <div className="mt-8 space-y-3.5 text-[15px] leading-[1.65] text-fd-text [&_b]:font-semibold [&_em]:italic [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
        {children}
      </div>
    </motion.section>
  );
}

// ── Video module ────────────────────────────────────────────────────────

/**
 * CaseStudyVideoModule — embedded video player + chapter list per case-study
 * section. Mirrors the Kickstart LessonPage video player for visual parity:
 * race-bib stamped card, big episode-number watermark, custom controls bar,
 * play/pause overlay. Production wires `<video>` source + poster — the demo
 * surface here ships the chrome with a placeholder gradient so the case
 * study reads end-to-end before the recordings land.
 *
 * Speaker attribution lands BELOW the player so the watcher knows whose
 * voice they're hearing without the chrome shouting it.
 */
function CaseStudyVideoModule({ video }: { video: VideoModule }) {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="grid gap-3 lg:grid-cols-[1.6fr_1fr] lg:gap-5">
      {/* Player */}
      <div
        className="relative overflow-hidden rounded-[14px] border-[1.5px] border-fd-text bg-fd-text"
        style={{ boxShadow: '5px 5px 0 0 var(--fd-text)' }}
      >
        <div className="relative aspect-video w-full">
          {/* Placeholder backdrop — replaced with poster + <video> in production */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(900px 360px at 30% 20%, oklch(0.30 0.005 95), oklch(0.16 0.005 95) 60%)',
            }}
          />
          {/* Episode-number watermark */}
          <span className="absolute left-6 top-4 font-geist-mono text-[64px] font-semibold leading-none tracking-[-0.03em] text-fd-accent/20">
            {video.num}
          </span>

          {/* Episode title overlay (top right) */}
          <div className="absolute right-5 top-4 max-w-[55%] text-right">
            <Eyebrow className="!text-fd-accent/70">EPISODE {video.num} · CASE STUDY</Eyebrow>
            <p className="mt-1 font-geist text-[13.5px] font-semibold leading-[1.25] text-fd-accent [text-wrap:balance]">
              {video.title}
            </p>
          </div>

          {/* Play / Pause overlay */}
          <button
            onClick={() => setIsPlaying((v) => !v)}
            aria-label={isPlaying ? 'Pause' : 'Play'}
            className="absolute inset-0 grid place-items-center transition-opacity hover:bg-fd-text/10"
          >
            <span
              className="grid h-20 w-20 place-items-center rounded-full border-[1.5px] border-fd-text bg-fd-accent text-fd-text transition-transform hover:scale-105"
              style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
            >
              {isPlaying ? <Pause className="h-7 w-7 fill-current" /> : <Play className="h-7 w-7 fill-current" />}
            </span>
          </button>

          {/* Custom controls bar */}
          <div className="absolute bottom-0 left-0 right-0 flex items-center gap-3 bg-gradient-to-t from-fd-text/85 to-transparent px-4 py-3">
            <span className="font-geist-mono text-[11px] font-bold text-fd-accent">
              {isPlaying ? '0:42' : '0:00'} / {video.duration}
            </span>
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/15">
              <div className="h-full bg-fd-accent" style={{ width: isPlaying ? '8%' : '0%' }} />
            </div>
            <button aria-label="Volume" className="text-fd-accent/80 hover:text-fd-accent">
              <Volume2 className="h-4 w-4" />
            </button>
            <button aria-label="Fullscreen" className="text-fd-accent/80 hover:text-fd-accent">
              <Maximize className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Speaker label band */}
        <div className="flex items-center justify-between gap-3 border-t-[1.5px] border-fd-accent/20 bg-fd-text px-4 py-2.5">
          <span className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-accent">
            {SPEAKER_LABEL[video.speaker]}
          </span>
          <span className="inline-flex items-center gap-1 font-geist-mono text-[10.5px] font-semibold text-fd-accent/70">
            <Clock className="h-3 w-3" /> {video.duration}
          </span>
        </div>
      </div>

      {/* Sidebar: hook + chapter list */}
      <aside className="flex flex-col gap-3">
        <div
          className="rounded-[12px] border-[1.5px] border-fd-text bg-fd-paper p-4"
          style={{ boxShadow: '3px 3px 0 0 var(--fd-text)' }}
        >
          <Eyebrow className="mb-1.5 block">WHY WATCH</Eyebrow>
          <p className="text-[13px] leading-[1.5] text-fd-text">{video.hook}</p>
        </div>

        <div className="rounded-[12px] border border-fd-border bg-fd-paper p-4">
          <Eyebrow className="mb-2 flex items-center gap-1.5">
            <ListVideo className="h-3.5 w-3.5" />
            CHAPTERS · {video.chapters.length}
          </Eyebrow>
          <ol className="space-y-1.5">
            {video.chapters.map((c) => (
              <li key={c.time} className="flex items-baseline gap-3 text-[12.5px] leading-[1.4]">
                <span className="w-10 shrink-0 font-geist-mono text-[10.5px] font-semibold text-fd-text-subtle">
                  {c.time}
                </span>
                <span className="text-fd-text">{c.label}</span>
              </li>
            ))}
          </ol>
        </div>
      </aside>
    </div>
  );
}

function PullQuote({ children, attribution }: { children: React.ReactNode; attribution: string }) {
  return (
    <blockquote
      className="relative my-6 rounded-[12px] border-l-[3px] border-fd-text bg-fd-paper py-5 pl-6 pr-5"
      style={{ boxShadow: '3px 3px 0 0 var(--fd-accent)' }}
    >
      <Quote
        aria-hidden="true"
        className="absolute -left-3 -top-3 h-6 w-6 rounded-[4px] border-[1.5px] border-fd-text bg-fd-accent p-0.5 text-fd-text"
      />
      <p className="font-instrument-serif text-[19px] italic leading-[1.5] text-fd-text [text-wrap:balance]">
        {children}
      </p>
      <footer className="mt-3 font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
        {attribution}
      </footer>
    </blockquote>
  );
}

function Inline({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded-[3px] bg-fd-text px-1.5 py-px font-geist-mono text-[12.5px] font-semibold text-fd-accent">
      {children}
    </code>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────

/** Parse "M:SS" to seconds. Returns 0 if malformed. */
function parseDuration(d: string): number {
  const [m, s] = d.split(':').map((n) => parseInt(n, 10));
  if (!Number.isFinite(m)) return 0;
  return m * 60 + (Number.isFinite(s) ? s : 0);
}

// keep unused-import suppressions for future iconography passes
void BookOpen; void Calendar; void Users; void TrendingUp;
